import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Medal, Award, Building2, CheckCircle2, XCircle, ArrowRight, Loader2, MailCheck } from "lucide-react";
import { Logo } from "@/components/Logo";
import { BrandBar } from "@/components/landing/BrandBar";
import { Footer } from "@/components/landing/Footer";
import { CertForm } from "@/components/landing/CertForm";
import api, { formatApiError } from "@/lib/api";

const CURRICULUM = [
  { week: "Week 1", title: "Fair Housing & Compliance Foundations", desc: "Protected classes, reasonable accommodations, assistance animals, advertising rules, and the enforcement landscape — with real 2026 DOJ cases." },
  { week: "Week 2", title: "The Walk Standard", desc: "Property, violation, move-in/move-out, make-ready, and due-diligence walks. Photo documentation that holds up in an audit." },
  { week: "Week 3", title: "Collections & Resident Communications", desc: "Delinquency cadences, notice accuracy, AI-drafted compliant resident communications, escalation discipline." },
  { week: "Week 4", title: "Make-Ready & Maintenance Operations", desc: "Turn-time benchmarks, vendor accountability, inspection completion, and owner-tour readiness." },
  { week: "Week 5", title: "Audits, Scoring & Corrective Action", desc: "Running the OPERA360 audit standard, scoring your own property, and closing corrective actions on deadline." },
  { week: "Week 6", title: "Capstone & Certification Exam", desc: "Full simulated community audit plus the proctored COC exam. Passing managers carry the credential." },
];

const QUIZ = [
  {
    q: "A resident with a mobility impairment requests a ground-floor unit. Several are available. What do you do?",
    options: ["Add them to the standard waitlist", "Process it promptly as a reasonable accommodation request", "Charge a unit-transfer fee", "Require detailed medical records first"],
    answer: 1,
    why: "Reasonable accommodation requests must be evaluated promptly — delaying 14 months cost one operator $750,000 in April 2026.",
  },
  {
    q: "Which of these is a protected class under the federal Fair Housing Act?",
    options: ["Source of income", "Familial status", "Credit score", "Pet ownership"],
    answer: 1,
    why: "Familial status, race, color, religion, sex, national origin, and disability are federally protected.",
  },
  {
    q: "How often should your community complete a documented violation walk?",
    options: ["When residents complain", "Weekly, per your operating standard", "Quarterly", "Only before owner tours"],
    answer: 1,
    why: "Properties running weekly documented walks show measurably fewer unresolved violations at audit.",
  },
  {
    q: "A prospect asks, 'Is this a safe neighborhood?' The compliant response is to…",
    options: ["Share your honest opinion", "Repeat crime statistics you heard", "Describe the property's actual features and point to objective public resources", "Refuse to answer and move on"],
    answer: 2,
    why: "Opinions about 'safety' are a classic steering trap. Stick to property features and objective sources.",
  },
  {
    q: "A structured 30/60/90-day onboarding program exists primarily to…",
    options: ["Delay full duties", "Create a consistent, documented ramp to your operating standard", "Satisfy HR paperwork", "Reduce training costs"],
    answer: 1,
    why: "Structured onboarding is why OPERA360 operators see dramatically lower first-year manager churn.",
  },
];

const PRICING = [
  {
    name: "Frontline COC",
    price: "$249",
    unit: "per manager · one-time",
    features: ["6-week cohort curriculum", "Fair-housing & compliance core", "Proctored certification exam", "Digital credential + registry listing"],
    testid: "cert-pricing-frontline",
  },
  {
    name: "Regional Advanced",
    price: "$599",
    unit: "per regional manager · one-time",
    features: ["Everything in Frontline COC", "Portfolio audit leadership", "Corrective-action program design", "AI Regional Manager mastery", "1:1 capstone review with faculty"],
    highlighted: true,
    testid: "cert-pricing-regional",
  },
  {
    name: "Portfolio Cohort",
    price: "$1,999",
    unit: "up to 12 seats · one-time",
    features: ["Private cohort for your organization", "Your SOPs built into the curriculum", "Property operational score included", "Executive results briefing"],
    testid: "cert-pricing-portfolio",
  },
];

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

const QuizCard = () => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [scoreEmail, setScoreEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const score = QUIZ.reduce((acc, q, i) => acc + (answers[i] === q.answer ? 1 : 0), 0);
  const verdict = score === 5 ? "Audit-ready. You'd pass the COC exam today." : score >= 3 ? "Solid instincts — certification makes them consistent." : "This is exactly the gap COC was built to close.";

  const sendScore = async () => {
    setSending(true);
    try {
      await api.post("/quiz/score-email", { email: scoreEmail, score });
      setSent(true);
      toast.success("Score sent", { description: "Check your inbox for your score and cohort-one invite." });
    } catch (err) {
      toast.error("Could not send score", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="technical-card p-6 sm:p-9" data-testid="cert-quiz">
      <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900 mb-1">Sample the Exam</p>
      <p className="text-sm text-muted-foreground mb-8">Five questions from the COC item bank. How would your managers score?</p>
      <div className="space-y-8">
        {QUIZ.map((q, qi) => (
          <div key={qi} data-testid={`cert-quiz-q-${qi}`}>
            <p className="text-sm font-semibold text-slate-900 mb-3">{qi + 1}. {q.q}</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {q.options.map((opt, oi) => {
                const chosen = answers[qi] === oi;
                const showCorrect = submitted && oi === q.answer;
                const showWrong = submitted && chosen && oi !== q.answer;
                return (
                  <button
                    key={oi}
                    type="button"
                    data-testid={`cert-quiz-q${qi}-opt${oi}`}
                    onClick={() => !submitted && setAnswers({ ...answers, [qi]: oi })}
                    className={`rounded-sm border px-3.5 py-2.5 text-left text-xs leading-relaxed transition-all duration-200 ${
                      showCorrect ? "border-moss/60 bg-moss/10 text-green-700"
                      : showWrong ? "border-red-500/60 bg-red-500/10 text-red-600"
                      : chosen ? "border-cadmium/60 bg-cadmium/10 text-sky-600"
                      : "border-slate-200 text-muted-foreground hover:border-white/25 hover:text-slate-900"
                    }`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
            {submitted && (
              <p className="mt-2.5 text-xs text-muted-foreground/80 leading-relaxed flex gap-2">
                {answers[qi] === q.answer
                  ? <CheckCircle2 size={13} className="mt-0.5 shrink-0 text-green-700" />
                  : <XCircle size={13} className="mt-0.5 shrink-0 text-red-600" />}
                {q.why}
              </p>
            )}
          </div>
        ))}
      </div>
      <div className="mt-9 flex flex-col sm:flex-row items-center gap-4">
        <button
          data-testid="cert-quiz-submit"
          onClick={() => setSubmitted(true)}
          disabled={Object.keys(answers).length < QUIZ.length || submitted}
          className="inline-flex items-center gap-2 rounded-md bg-cadmium px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-cadmium-hover active:scale-[0.98] disabled:opacity-50"
        >
          Score My Answers
        </button>
        {submitted && (
          <p data-testid="cert-quiz-result" className="text-sm">
            <span className="font-mono text-2xl font-semibold text-sky-600">{score}/5</span>
            <span className="ml-3 text-muted-foreground">{verdict}</span>
          </p>
        )}
      </div>

      {submitted && (
        <div className="mt-7 border-t border-slate-200 pt-6" data-testid="quiz-email-block">
          <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900 mb-1.5">
            Get this score in your inbox
          </p>
          <p className="text-xs text-muted-foreground mb-4">
            We'll email your result with a cohort-one waitlist invite you can forward to your team.
          </p>
          {sent ? (
            <p data-testid="quiz-email-success" className="flex items-center gap-2 text-sm text-green-700">
              <MailCheck size={15} /> Sent — check your inbox, then{" "}
              <a href="#cert-enroll" className="underline underline-offset-2 hover:text-sky-600 transition-colors">join the waitlist</a>.
            </p>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                data-testid="quiz-email-input"
                type="email"
                required
                value={scoreEmail}
                onChange={(e) => setScoreEmail(e.target.value)}
                placeholder="you@portfolio.com"
                className="flex-1 rounded-md border border-slate-200 bg-[#EDF4FB] px-4 py-3 text-sm text-slate-900 placeholder:text-muted-foreground/50 focus:outline-none focus:border-cadmium/60 transition-all"
              />
              <button
                data-testid="quiz-email-submit"
                onClick={sendScore}
                disabled={sending || !scoreEmail.includes("@")}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-cadmium/60 px-5 py-3 text-sm font-semibold text-sky-600 transition-all duration-200 hover:bg-cadmium hover:text-white active:scale-[0.98] disabled:opacity-50"
              >
                {sending && <Loader2 size={14} className="animate-spin" />}
                Email My Score
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function Certification() {
  return (
    <div className="workspace-light bg-[#EDF4FB] text-slate-900 min-h-screen" data-testid="certification-page">
      <BrandBar />
      <header className="glass-nav h-16 sm:h-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between">
          <Link to="/" data-testid="cert-nav-logo" aria-label="OPERA360 home"><Logo /></Link>
          <div className="flex items-center gap-6">
            <Link to="/" className="hidden sm:block text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-sky-600 transition-colors">
              ← Back to site
            </Link>
            <a
              href="#cert-enroll"
              data-testid="cert-nav-reserve-cta"
              className="inline-flex items-center rounded-md bg-cadmium px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-cadmium-hover hover:-translate-y-0.5"
            >
              Reserve a Seat
            </a>
          </div>
        </div>
      </header>

      <main className="pt-28 sm:pt-36">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          <motion.div {...reveal} className="max-w-3xl">
            <p className="eyebrow mb-4 flex items-center gap-3">
              <span className="inline-block h-px w-10 bg-cadmium" /> Community Operations Certification
            </p>
            <h1 className="font-cabinet text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-slate-900">
              The credential that says your team{" "}
              <span className="font-editorial italic font-normal text-sky-600">runs the standard.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              COC is a six-week, cohort-based certification for community managers and regional leaders in manufactured
              housing, multifamily, RV, and single-family operations — built from the curriculum Christopher Love runs
              across a 58-community portfolio.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#cert-enroll" data-testid="cert-hero-reserve-cta" className="group inline-flex items-center gap-2 rounded-md bg-cadmium px-7 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-cadmium-hover hover:-translate-y-0.5">
                Join the Waitlist <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </a>
              <a href="#cert-quiz" className="inline-flex items-center rounded-md border border-slate-300 px-7 py-3.5 text-sm font-semibold text-slate-900 transition-all duration-200 hover:border-cadmium/60 hover:text-sky-600">
                Try the Sample Quiz
              </a>
            </div>
          </motion.div>
        </section>

        <section id="cert-curriculum" className="border-t border-slate-200 bg-[#E2EDF8] py-20 sm:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div {...reveal} className="max-w-2xl mb-12">
              <p className="eyebrow mb-4 flex items-center gap-3">
                <span className="inline-block h-px w-10 bg-cadmium" /> Curriculum
              </p>
              <h2 className="font-cabinet text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">
                Six weeks. <span className="font-editorial italic font-normal text-sky-600">Zero tribal knowledge.</span>
              </h2>
            </motion.div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {CURRICULUM.map((c, i) => (
                <motion.div key={c.week} {...reveal} transition={{ ...reveal.transition, delay: i * 0.06 }}
                  data-testid={`cert-curriculum-${i}`} className="technical-card p-6">
                  <p className="text-[10px] font-mono font-semibold uppercase tracking-[0.2em] text-sky-600 mb-3">{c.week}</p>
                  <p className="text-base font-bold text-slate-900 mb-2">{c.title}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section id="cert-quiz" className="border-t border-slate-200 py-20 sm:py-28">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div {...reveal}>
              <QuizCard />
            </motion.div>
          </div>
        </section>

        <section id="cert-pricing" className="border-t border-slate-200 bg-[#E2EDF8] py-20 sm:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div {...reveal} className="max-w-2xl mb-12">
              <p className="eyebrow mb-4 flex items-center gap-3">
                <span className="inline-block h-px w-10 bg-cadmium" /> Cohort One Pricing
              </p>
              <h2 className="font-cabinet text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">
                Certification pays for itself in{" "}
                <span className="font-editorial italic font-normal text-sky-600">one avoided complaint.</span>
              </h2>
              <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                One fair-housing defense runs $50K–$200K+. One manager replacement runs $27K–$110K. Cohort one pricing
                is deliberately small against that math.
              </p>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
              {PRICING.map((t, i) => (
                <motion.div key={t.name} {...reveal} transition={{ ...reveal.transition, delay: i * 0.1 }}
                  data-testid={t.testid}
                  className={`relative rounded-md p-7 sm:p-8 flex flex-col border transition-all duration-300 ${
                    t.highlighted
                      ? "bg-gradient-to-b from-white to-[#E2EDF8] border-cadmium/60 shadow-[0_0_60px_-15px_rgba(46,156,255,0.35)]"
                      : "bg-white border-slate-200 hover:border-slate-300"
                  }`}
                >
                  {t.highlighted && (
                    <span className="absolute -top-3 left-6 rounded-sm bg-cadmium px-2.5 py-1 text-[10px] font-mono font-semibold uppercase tracking-widest text-white">
                      Leadership Track
                    </span>
                  )}
                  <h3 className="font-cabinet text-xl font-bold text-slate-900 mt-1">{t.name}</h3>
                  <p className="mt-4 font-mono text-4xl font-semibold text-slate-900 tracking-tight">{t.price}</p>
                  <p className="mt-1.5 text-xs font-mono uppercase tracking-wider text-muted-foreground">{t.unit}</p>
                  <ul className="mt-7 space-y-3 flex-1">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-slate-900/85">
                        <Medal size={14} className={`mt-0.5 shrink-0 ${t.highlighted ? "text-sky-600" : "text-green-700"}`} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <a href="#cert-enroll" data-testid={`${t.testid}-cta`}
                    className={`mt-8 inline-flex items-center justify-center rounded-md px-5 py-3 text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 ${
                      t.highlighted
                        ? "bg-cadmium text-white hover:bg-cadmium-hover"
                        : "border border-slate-300 text-slate-900 hover:border-cadmium/60 hover:text-sky-600"
                    }`}
                  >
                    Reserve a Seat
                  </a>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section id="cert-enroll" className="border-t border-slate-200 py-20 sm:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
            <motion.div {...reveal} className="lg:col-span-5">
              <p className="eyebrow mb-4 flex items-center gap-3">
                <span className="inline-block h-px w-10 bg-cadmium" /> Enroll
              </p>
              <h2 className="font-cabinet text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">
                Cohort one is <span className="font-editorial italic font-normal text-sky-600">limited by design.</span>
              </h2>
              <p className="mt-5 text-base text-muted-foreground leading-relaxed">
                We cap enrollment so every capstone audit gets a real review. Waitlist members get first access,
                launch pricing locked, and a free portfolio readiness checklist.
              </p>
              <div className="mt-7 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-sm border border-cadmium/40 bg-cadmium/10 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest text-sky-600">
                  <Award size={11} /> Launch pricing locked
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-sm border border-moss/40 bg-moss/10 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest text-green-700">
                  <Building2 size={11} /> Team seats available
                </span>
              </div>
            </motion.div>
            <motion.div {...reveal} className="lg:col-span-7">
              <CertForm />
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
